/**
 * Unified Inbox — two tabs: Trainers | Buyers & Sellers.
 *
 * Trainers tab reuses the existing /api/pro/threads (both pro & member perspectives).
 * Buyers & Sellers tab uses the new /api/marketplace/threads.
 *
 * Route: /inbox
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Send, Inbox as InboxIcon } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import BottomNav from "../../components/layout/BottomNav";
import { toast } from "sonner";
import { detectContactLeak } from "../../utils/contactFilter";
import ContactLeakBanner from "../../components/chat/ContactLeakBanner";

const TABS = [
  { id: "trainers", label: "Trainers" },
  { id: "marketplace", label: "Buyers & Sellers" },
];

export default function Inbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get("tab") === "marketplace" ? "marketplace" : "trainers";
  const [tab, setTab] = useState(initialTab);
  const [activeId, setActiveId] = useState(searchParams.get("thread") || null);

  const onTabChange = (t) => {
    setTab(t);
    setActiveId(null);
    setSearchParams({ tab: t });
  };

  return (
    <div className="app-page min-h-screen" data-testid="inbox-page" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button
            onClick={() => (activeId ? setActiveId(null) : navigate(-1))}
            aria-label="Back"
            data-testid="inbox-back"
            className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/60"
          >
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-barlow-cond font-extrabold text-[22px]">Messages</h1>
        </div>
        {!activeId && (
          <div className="max-w-2xl mx-auto px-4 pb-3 flex gap-2" data-testid="inbox-tabs">
            {TABS.map((t) => {
              const on = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => onTabChange(t.id)}
                  data-testid={`inbox-tab-${t.id}`}
                  className="shrink-0"
                  style={{
                    background: on ? "#00D4FF" : "#ECEAE7",
                    color: on ? "#0B0B0B" : "#6B6760",
                    border: on ? "1px solid #00D4FF" : "1px solid #E2E0DC",
                    height: 34,
                    borderRadius: 100,
                    padding: "0 16px",
                    fontFamily: "Manrope",
                    fontSize: 13,
                    fontWeight: on ? 700 : 600,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-3 pb-28">
        {activeId ? (
          tab === "marketplace" ? (
            <MarketplaceThreadDetail
              threadId={activeId}
              onBack={() => setActiveId(null)}
            />
          ) : (
            <TrainerThreadDetail
              threadId={activeId}
              onBack={() => setActiveId(null)}
            />
          )
        ) : tab === "marketplace" ? (
          <MarketplaceThreadsList onOpen={setActiveId} />
        ) : (
          <TrainerThreadsList onOpen={setActiveId} />
        )}
      </main>

      <BottomNav />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Marketplace — threads + detail
// ─────────────────────────────────────────────────────────────
function MarketplaceThreadsList({ onOpen }) {
  const [threads, setThreads] = useState(null);

  useEffect(() => {
    apiClient
      .get("/marketplace/threads")
      .then((r) => setThreads(r.data || []))
      .catch(() => setThreads([]));
  }, []);

  if (threads === null) return <ListSkeleton />;
  if (threads.length === 0) {
    return (
      <EmptyState
        title="No marketplace messages yet"
        sub="Message a seller from a product detail page to start a thread."
      />
    );
  }

  return (
    <div className="space-y-2" data-testid="marketplace-threads-list">
      {threads.map((t) => (
        <button
          key={t.thread_id}
          onClick={() => onOpen(t.thread_id)}
          data-testid={`mk-thread-${t.thread_id}`}
          className="w-full flex items-center gap-3 bg-white rounded-[14px] p-3 border border-line2 text-left"
        >
          <div className="w-12 h-12 rounded-[10px] overflow-hidden bg-canvas2 shrink-0">
            {t.product_snapshot?.photo && (
              <img src={t.product_snapshot.photo} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-semibold truncate">
                {t.counterpart_name}
              </span>
              <span
                className="text-[9px] font-bold uppercase px-1.5 h-[18px] inline-flex items-center rounded-pill"
                style={{
                  background: t.role === "seller" ? "rgba(0,180,100,0.12)" : "rgba(0,176,213,0.14)",
                  color: t.role === "seller" ? "rgb(0,150,80)" : "#00B0D5",
                  borderRadius: 100,
                  letterSpacing: "0.04em",
                }}
              >
                {t.role === "seller" ? "Buyer" : "Seller"}
              </span>
            </div>
            <div className="text-[12px] text-ink-soft truncate">
              {t.product_snapshot?.title}
            </div>
            <div className="text-[12px] text-ink-tertiary truncate mt-0.5">
              {t.last_message_text}
            </div>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            <span className="text-[10px] text-ink-tertiary">
              {timeAgo(t.last_message_at)}
            </span>
            {t.unread && (
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: "#00D4FF" }}
                data-testid="unread-dot"
              />
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

export function MarketplaceThreadDetail({ threadId, onBack }) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/marketplace/threads/${threadId}`);
      setThread(data.thread);
      setMessages(data.messages || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch {
      toast.error("Could not load thread");
    }
  };

  useEffect(() => {
    load();
  }, [threadId]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/marketplace/threads/${threadId}/messages`, { text });
      setMessages((m) => [...m, data]);
      setDraft("");
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 30);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error(detail || "Slow down — max 10 messages per hour");
      else if (status === 400) toast.error(detail || "Message not allowed");
      else toast.error("Could not send");
    } finally {
      setBusy(false);
    }
  };

  if (!thread) return <ListSkeleton />;

  const snap = thread.product_snapshot || {};
  const soldOut = snap.status && snap.status !== "active";

  return (
    <div data-testid="marketplace-thread-detail">
      {/* Product header card — context for all messages */}
      <button
        type="button"
        onClick={() => navigate(`/marketplace/product/${snap.product_id}`)}
        data-testid="thread-product-header"
        className="w-full text-left bg-white rounded-[14px] p-3 border border-line2 flex items-center gap-3"
      >
        <div className="w-14 h-14 rounded-[10px] overflow-hidden bg-canvas2 shrink-0">
          {snap.photo && <img src={snap.photo} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold line-clamp-1">{snap.title}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-barlow-cond font-black text-[18px]">
              ₹{(snap.price_inr || 0).toLocaleString("en-IN")}
            </span>
            {snap.condition && (
              <span
                className="text-[10px] font-bold uppercase px-2 h-5 inline-flex items-center rounded-pill"
                style={{ background: "#ECEAE7", borderRadius: 100 }}
              >
                {snap.condition.replace("_", " ")}
              </span>
            )}
            {soldOut && (
              <span
                className="text-[10px] font-bold uppercase px-2 h-5 inline-flex items-center rounded-pill"
                style={{
                  background: "rgba(230,100,100,0.12)",
                  color: "rgb(200,70,70)",
                  borderRadius: 100,
                }}
              >
                {snap.status}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Messages scroller */}
      <div
        ref={scrollRef}
        className="mt-3 bg-white rounded-[14px] p-3 border border-line2 space-y-2 overflow-y-auto"
        style={{ maxHeight: "50vh", minHeight: 220 }}
        data-testid="thread-messages"
      >
        {messages.map((m) => {
          const mine = m.sender_id === user?.user_id;
          if (m.is_system) {
            return (
              <div key={m.message_id} className="flex justify-center">
                <div
                  className="text-[12px] text-center max-w-[80%] px-3 py-1.5"
                  style={{
                    background: "rgba(0,212,255,0.08)",
                    color: "#00B0D5",
                    borderRadius: 10,
                  }}
                >
                  {m.text}
                </div>
              </div>
            );
          }
          return (
            <div
              key={m.message_id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`mk-msg-${m.message_id}`}
            >
              <div
                className="max-w-[78%] px-3 py-2 text-[13px]"
                style={{
                  background: mine ? "#00D4FF" : "#ECEAE7",
                  color: mine ? "#000" : "var(--text-primary)",
                  borderRadius: 14,
                  borderBottomRightRadius: mine ? 4 : 14,
                  borderBottomLeftRadius: mine ? 14 : 4,
                }}
              >
                {m.text}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer — plain text only, no media */}
      <ContactLeakBanner rule={detectContactLeak(draft)} />
      <div className="mt-3 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 500))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!detectContactLeak(draft)) send();
            }
          }}
          placeholder="Type a message…"
          data-testid="mk-thread-input"
          className="flex-1 h-12 px-3 rounded-pill border border-line bg-white text-[14px] outline-none"
          style={{ borderRadius: 100 }}
        />
        <button
          onClick={send}
          disabled={busy || !draft.trim() || !!detectContactLeak(draft)}
          data-testid="mk-thread-send"
          className="w-12 h-12 rounded-full grid place-items-center disabled:opacity-50"
          style={{ background: "#00D4FF", color: "#000" }}
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </div>
      <p className="text-[10px] text-ink-tertiary text-center mt-2">
        Phone numbers aren't allowed here — contact details are shared after a successful purchase.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Trainers — threads + detail (reuses /api/pro/threads)
// ─────────────────────────────────────────────────────────────
function TrainerThreadsList({ onOpen }) {
  const user = useAuthStore((s) => s.user);
  const [threads, setThreads] = useState(null);

  useEffect(() => {
    apiClient
      .get("/pro/threads")
      .then((r) => setThreads(r.data || []))
      .catch(() => setThreads([]));
  }, []);

  if (threads === null) return <ListSkeleton />;
  if (threads.length === 0) {
    return (
      <EmptyState
        title="No trainer threads yet"
        sub="Send an enquiry from any trainer profile to start chatting."
      />
    );
  }

  return (
    <div className="space-y-2" data-testid="trainer-threads-list">
      {threads.map((t) => {
        const iAmPro = t.professional_user_id === user?.user_id;
        const name = iAmPro ? t.member_name : t.professional_name;
        const unread = iAmPro ? t.unread_for_pro : t.unread_for_member;
        return (
          <button
            key={t.thread_id}
            onClick={() => onOpen(t.thread_id)}
            data-testid={`tr-thread-${t.thread_id}`}
            className="w-full flex items-center gap-3 bg-white rounded-[14px] p-3 border border-line2 text-left"
          >
            <div
              className="w-11 h-11 rounded-full grid place-items-center shrink-0 text-[14px] font-semibold"
              style={{ background: "var(--accent-blue-dim)" }}
            >
              {(name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{name}</div>
              <div className="text-[12px] text-ink-soft truncate">
                {t.last_message_preview}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1">
              <span className="text-[10px] text-ink-tertiary">
                {timeAgo(t.last_message_at)}
              </span>
              {unread && (
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: "#00D4FF" }}
                />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TrainerThreadDetail({ threadId, onBack }) {
  const user = useAuthStore((s) => s.user);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/pro/threads/${threadId}`);
      setThread(data.thread);
      setMessages(data.messages || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch {
      toast.error("Could not load thread");
    }
  };

  useEffect(() => {
    load();
  }, [threadId]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/pro/threads/${threadId}/messages`, { text });
      setMessages((m) => [...m, data]);
      setDraft("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "contact_blocked") {
        toast.error(detail.message || "Contact details cannot be shared here.");
      } else {
        toast.error("Could not send");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!thread) return <ListSkeleton />;
  const iAmPro = thread.professional_user_id === user?.user_id;
  const counterpartName = iAmPro ? thread.member_name : thread.professional_name;

  return (
    <div data-testid="trainer-thread-detail">
      <div className="bg-white rounded-[14px] p-3 border border-line2 flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-full grid place-items-center shrink-0 text-[14px] font-semibold"
          style={{ background: "var(--accent-blue-dim)" }}
        >
          {(counterpartName || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold">{counterpartName}</div>
          <div className="text-[11px] text-ink-soft">Trainer conversation</div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="mt-3 bg-white rounded-[14px] p-3 border border-line2 space-y-2 overflow-y-auto"
        style={{ maxHeight: "55vh", minHeight: 220 }}
      >
        {messages.map((m) => {
          const mine = m.sender_user_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className="max-w-[78%] px-3 py-2 text-[13px]"
                style={{
                  background: mine ? "#00D4FF" : "#ECEAE7",
                  color: mine ? "#000" : "var(--text-primary)",
                  borderRadius: 14,
                }}
              >
                {m.text}
              </div>
            </div>
          );
        })}
      </div>
      <ContactLeakBanner rule={detectContactLeak(draft)} />
      <div className="mt-3 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!detectContactLeak(draft)) send();
            }
          }}
          placeholder="Type a message…"
          data-testid="tr-thread-input"
          className="flex-1 h-12 px-3 rounded-pill border border-line bg-white text-[14px] outline-none"
          style={{ borderRadius: 100 }}
        />
        <button
          onClick={send}
          disabled={busy || !draft.trim() || !!detectContactLeak(draft)}
          data-testid="tr-thread-send"
          className="w-12 h-12 rounded-full grid place-items-center disabled:opacity-50"
          style={{ background: "#00D4FF", color: "#000" }}
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function ListSkeleton() {
  return (
    <div className="space-y-2" data-testid="inbox-loading">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-16 bg-white rounded-[14px] border border-line2 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState({ title, sub }) {
  return (
    <div className="text-center py-16 px-6" data-testid="inbox-empty">
      <div
        className="w-14 h-14 mx-auto rounded-full grid place-items-center"
        style={{ background: "rgba(0,212,255,0.12)", color: "#00B0D5" }}
      >
        <InboxIcon size={20} />
      </div>
      <h3 className="font-barlow-cond font-extrabold text-[20px] mt-3">{title}</h3>
      <p className="text-[13px] text-ink-soft mt-1">{sub}</p>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
